import { useCallback, useEffect, useRef, useState, type DragEvent } from 'react';
import { createItems, titleFromFilename, type ItemDraft } from '../db/items';
import type { Container } from '../db/board';
import { couldntReadLabel } from '../ui/counts';

/**
 * Dropping files on the board and pasting an image both land here. For a
 * personal tool the drop is the way you actually use it — the dialog is for
 * when the photo is already somewhere tidy.
 *
 * One item per file, titled from the filename with its extension stripped.
 */
export function useFileIntake({
  container,
  enabled,
  onCreated,
}: {
  /** Where a dropped file lands: the room, or the section you're inside. */
  container: Container | undefined;
  /** False while a dialog owns the keyboard — a paste there belongs to it. */
  enabled: boolean;
  onCreated: () => void;
}) {
  const [dragging, setDragging] = useState(false);
  const [adding, setAdding] = useState(0);
  const [error, setError] = useState<string | undefined>(undefined);

  // dragenter/dragleave fire for every child the cursor crosses; counting them
  // is what stops the overlay strobing as the pointer moves over tiles.
  const depth = useRef(0);

  const add = useCallback(
    async (drafts: readonly ItemDraft[]) => {
      if (container === undefined || drafts.length === 0) return;

      setAdding(drafts.length);
      setError(undefined);
      try {
        const result = await createItems(container, drafts);
        if (result.failed.length > 0) setError(couldntReadLabel(result.failed));
        if (result.created.length > 0) onCreated();
      } catch {
        setError("Those photos couldn't be added. Nothing was saved.");
      } finally {
        setAdding(0);
      }
    },
    [container, onCreated],
  );

  useEffect(() => {
    if (!enabled) return;

    function onPaste(event: ClipboardEvent) {
      // A paste inside a text field is that field's, not the board's.
      const target = event.target;
      if (target instanceof HTMLElement && target.closest('input, textarea, [contenteditable]')) {
        return;
      }

      const drafts = draftsFrom(event.clipboardData?.files);
      if (drafts.length === 0) return;
      event.preventDefault();
      void add(drafts);
    }

    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [enabled, add]);

  function reset() {
    depth.current = 0;
    setDragging(false);
  }

  const handlers = {
    onDragEnter: (event: DragEvent) => {
      if (!carriesFiles(event)) return;
      depth.current += 1;
      setDragging(true);
    },
    onDragOver: (event: DragEvent) => {
      if (!carriesFiles(event)) return;
      // Without this the browser navigates to the dropped file.
      event.preventDefault();
      event.dataTransfer.dropEffect = 'copy';
    },
    onDragLeave: () => {
      depth.current -= 1;
      if (depth.current <= 0) reset();
    },
    onDrop: (event: DragEvent) => {
      if (!carriesFiles(event)) return;
      event.preventDefault();
      reset();
      void add(draftsFrom(event.dataTransfer.files));
    },
  };

  return { dragging, adding, error, dismissError: () => setError(undefined), handlers };
}

export type FileIntake = ReturnType<typeof useFileIntake>;

function carriesFiles(event: DragEvent): boolean {
  return event.dataTransfer.types.includes('Files');
}

function draftsFrom(files: FileList | undefined | null): ItemDraft[] {
  if (files === null || files === undefined) return [];

  return [...files]
    .filter((file) => file.type.startsWith('image/'))
    .map((file) => ({ title: title(file), cover: file }));
}

function title(file: File): string {
  const fromName = titleFromFilename(file.name);
  // What Chromium calls a screenshot off the clipboard. It names nothing.
  return fromName === 'image' ? 'Pasted Item' : fromName;
}
